/* eslint-disable no-undef */
import axios from 'axios';
import { trackPromise } from 'react-promise-tracker';
import { store } from 'redux/store';
import { handleRefreshToken } from './handleRefreshToken';

// urls
export const BASE_URL =
  (process.env.REACT_APP_NOD_ENV === 'development' && process.env.REACT_APP_DEVELOPMENT_BASE_URL) ||
  (process.env.REACT_APP_NOD_ENV === 'local' && process.env.REACT_APP_LOCAL_BASE_URL) ||
  (process.env.REACT_APP_NOD_ENV === 'staging' && process.env.REACT_APP_STAGING_BASE_URL) ||
  (process.env.REACT_APP_NOD_ENV === 'production' && process.env.REACT_APP_PRODUCTION_BASE_URL);
export const DOMAIN =
  (process.env.REACT_APP_NOD_ENV === 'development' && process.env.REACT_APP_DEVELOPMENT_DOMAIN) ||
  (process.env.REACT_APP_NOD_ENV === 'local' && process.env.REACT_APP_LOCAL_DOMAIN) ||
  (process.env.REACT_APP_NOD_ENV === 'staging' && process.env.REACT_APP_STAGING_DOMAIN) ||
  (process.env.REACT_APP_NOD_ENV === 'production' && process.env.REACT_APP_PRODUCTION_DOMAIN);
export const WEBADDRESS = process.env.REACT_APP_WEB_ADDRESS;
export const SIGNAL_URL =
  (process.env.REACT_APP_NOD_ENV === 'development' && process.env.REACT_APP_DEVELOPMENT_SIGNAL_URL) ||
  (process.env.REACT_APP_NOD_ENV === 'local' && process.env.REACT_APP_LOCAL_SIGNAL_URL) ||
  (process.env.REACT_APP_NOD_ENV === 'staging' && process.env.REACT_APP_STAGING_SIGNAL_URL) ||
  (process.env.REACT_APP_NOD_ENV === 'production' && process.env.REACT_APP_PRODUCTION_SIGNAL_URL);

export let errorResponse = {};

export const METHOD = {
  GET: 'get',
  HEAD: 'HEAD',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
  POST: 'POST',
};

const singleton = Symbol();
const singletonEnforcer = Symbol();

const defaultOptions = {
  baseURL: BASE_URL,
  method: METHOD.GET,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
};

export class ApiService {
  constructor() {
    // if (enforcer !== singletonEnforcer) {
    //   throw new Error('Cannot construct singleton');
    // }
    const defaultOptions = {
      baseURL: BASE_URL,
      method: METHOD.GET,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    this.controller = new AbortController();
    this.session = axios.create(defaultOptions);
    this.session.interceptors.request.use((config) => {
      // const getToken = cookie.get('token');
      const localStorageToken = localStorage.getItem('sessionid');
      config.headers['Authorization'] = localStorageToken;
      config.params = Object.assign({}, config.params || {});

      return config;
    });

    this.session.interceptors.response.use(
      (response) => {
        errorResponse = {};
        return response.data;
      },
      (error) => {
        if (error?.response?.status === 401) {
          const refreshToken = localStorage.getItem('refreshToken');
          const localStorageToken = localStorage.getItem('sessionid');
          const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
          const isTokenExpired = !tokenExpiresAt || Date.now() > +tokenExpiresAt;
          const needTokenRefresh = isTokenExpired && refreshToken && localStorageToken;

          if (needTokenRefresh) {
            store.dispatch(handleRefreshToken(refreshToken, localStorageToken));
          }
        }
        errorResponse.error = error.response;
        errorResponse.networkError = JSON.parse(JSON.stringify(error));
        return Promise.reject(error?.response?.data);
      }
    );
  }

  static get instance() {
    if (!this[singleton]) {
      this[singleton] = new ApiService(singletonEnforcer);
    }

    return this[singleton];
  }

  /*eslint-disable */
  get = (url, options = {}) => trackPromise(this.session.get(url, { ...defaultOptions, signal: this.controller.signal, ...options }));
  getWithoutLoading = (url, options = {}) => this.session.get(url, { ...defaultOptions, signal: this.controller.signal, ...options });
  post = (url, data, options = {}) => trackPromise(this.session.post(url, data, { ...defaultOptions, signal: this.controller.signal, ...options }));
  postWithoutLoading = (url, data, options = {}) => this.session.post(url, data, { ...defaultOptions, signal: this.controller.signal, ...options });
  put = (url, data, options = {}) => trackPromise(this.session.put(url, data, { ...defaultOptions, signal: this.controller.signal, ...options }));
  putWithoutLoading = (url, data, options = {}) => this.session.put(url, data, { ...defaultOptions, signal: this.controller.signal, ...options });
  patch = (url, data, options = {}) => trackPromise(this.session.patch(url, data, { ...defaultOptions, signal: this.controller.signal, ...options }));
  delete = (url, options = {}) => trackPromise(this.session.delete(url, { ...defaultOptions, signal: this.controller.signal, ...options }));
  remove = (...params) => this.session.delete(...params);
  abort = () => {
    this.controller.abort();
    this.controller = new AbortController();
  };

  downloadFile = async (url, options = {}) => {
    try {
      const response = await this.session.get(url, {
        ...defaultOptions,
        responseType: 'blob',
        signal: this.controller.signal,
        ...options,
      });

      const blob = new Blob([response], { type: response.type || 'application/octet-stream' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers?.['content-disposition'];
      let filename = 'file.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error(' Error downloading file:', err);
      throw err;
    }
  };
}

export default ApiService.instance;
